import React, { useState } from 'react';
import { AlertTriangle, BadgeCheck } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { MoneyInput } from '@components/MoneyInput';
import creditCardService from '@services/creditCardService';
import type { CreditCardInvoice } from '@/types/finance';
import { formatDateBR, todayISO } from '@/utils/formatters';
import { formatBRL } from '@/utils/money';
import { Required, labelCls } from './financeBits';

interface Props {
  invoice: CreditCardInvoice;
  onClose: () => void;
  onSaved: (invoice: CreditCardInvoice) => void;
  onError: (err: unknown) => void;
}

/**
 * Pagar fatura (POST /credit-cards/invoices/:id/pay): data, valor (pré-preenchido com o total;
 * menor → a diferença vira "Saldo anterior" na fatura seguinte) e juros/encargos opcionais (viram
 * item "Juros e encargos" na seguinte). O espelho na conta de pagamento passa a ter a data e o
 * valor pagos.
 */
export const PayInvoiceModal: React.FC<Props> = ({ invoice, onClose, onSaved, onError }) => {
  const total = Math.abs(invoice.total_cents);
  const [saving, setSaving] = useState(false);
  const [paidAt, setPaidAt] = useState(todayISO());
  const [amount, setAmount] = useState<number | null>(total || null);
  const [interest, setInterest] = useState<number | null>(null);

  const diff = amount !== null ? total - amount : 0;
  const canSubmit = !saving && !!paidAt && !!amount && amount > 0;

  const save = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      onSaved(await creditCardService.pay(invoice.id, { paid_at: paidAt, amount_cents: amount as number, interest_cents: interest ?? 0 }));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <BadgeCheck size={18} className="text-slate-500" />
        <h2 className="text-h3">Pagar fatura {invoice.label}</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-3">
        <p className="text-sm text-slate-600">
          {invoice.credit_card_name} · total <strong>{formatBRL(total)}</strong> · vencimento {formatDateBR(invoice.due_date)}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>
              Data do pagamento
              <Required />
            </label>
            <DateInputBR className="input-field w-full" value={paidAt} onChange={setPaidAt} />
          </div>
          <div>
            <label className={labelCls}>
              Valor pago
              <Required />
            </label>
            <MoneyInput value={amount} onChange={setAmount} autoFocus />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Juros e encargos (opcional)</label>
            <MoneyInput value={interest} onChange={setInterest} />
            <p className="text-small mt-1">Entram como item "Juros e encargos" na fatura seguinte (categoria Juros e encargos).</p>
          </div>
        </div>
        {diff > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <span>
              Pagamento parcial: a diferença de <strong>{formatBRL(diff)}</strong> vai para a próxima fatura como "Saldo anterior".
            </span>
          </div>
        )}
        {diff < 0 && (
          <p className="text-small">Valor maior que o total da fatura: será registrado como informado.</p>
        )}
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          Registrar pagamento
        </Button>
      </div>
    </Modal>
  );
};
